import { computed, ref, watch } from "vue";
import { apolloClient } from "@/utils/apollo";
import { graphql as gql } from "@/generated";
import type { GeoLocation } from "@/generated/graphql";
import { countries, type CountryCode } from "@/constants/country";
import { useAwaitAction } from "./use-await-action";

const LocationDocument = gql(/* GraphQL */ `
  query GetLocation($ip: String!) {
    getLocation(ip: $ip) {
      city
      region
      country
      countryCode
      lat
      lon
    }
  }
`);

export function useLocation() {
  const ip = ref<string | null>(null);
  const location = ref<GeoLocation | null>(null);
  const countryCode = computed(
    () => location.value?.countryCode as CountryCode | undefined,
  );

  const {
    handler: fetchLocation,
    loading: loading,
    error: error,
  } = useAwaitAction(async () => {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    const response = await fetch("https://api.ipify.org?format=json");
    const json = await response.json();

    if (!json.ip) throw new Error("IP not found");

    ip.value = json.ip as string;
    const query = await apolloClient.query({
      query: LocationDocument,
      variables: { ip: ip.value },
    });

    const result = query.data.getLocation;

    if (result) {
      location.value = result;
    } else {
      throw new Error("Location not found");
    }
  });

  function watchLocationCountry(
    listener: (country: keyof typeof countries) => void,
  ) {
    watch(
      () => location.value?.countryCode,
      (value) => {
        if (value && Object.keys(countries).includes(value)) {
          listener(value as keyof typeof countries);
        }
      },
    );
  }

  return {
    ip,
    location,
    countryCode,
    loading,
    error,
    fetchLocation,
    watchLocationCountry,
  };
}
